/**
 * NOLAS 2026, © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/hooks/useBrandingLogos.ts
 *
 * Rôle :
 *   Lit les logos du bandeau (Header public + AdminShell back-office) depuis
 *   le setting public JSON `branding.header_logos` (V73), une LISTE de
 *   `{url, alt}` de taille LIBRE, 100 % configurable au back-office
 *   `/admin/settings` → section « Marque » (upload direct + ajout/suppression
 *   illimités). Clone multi-salon en une fraction de seconde, SANS rebuild.
 *
 * Multi-salon :
 *   AUCUN logo en dur. Repli = liste VIDE → jamais le logo d'un autre salon.
 *   Pattern requête = SelectionRolePage (V31).
 *
 * Tolérant offline-first (§18) : endpoint absent / HS / clé non seedée
 *   (V73 pas encore appliquée) / JSON invalide → `[]`. retry:false.
 *
 * @author Mika
 * @since 2026-07-06 (garde ouverture, personnalisation multi-salon)
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/api/client';

/** Un logo de marque affiché dans le bandeau. */
export interface BrandingLogo {
  readonly url: string;
  readonly alt: string;
}

/** Clé setting publique lue (V73). */
const SETTING_KEY = 'branding.header_logos';

/** Parse tolérant de la valeur JSON du setting → liste de logos non vides. */
function parseLogos(raw: unknown): BrandingLogo[] {
  if (typeof raw !== 'string' || raw.trim() === '') return [];
  let arr: unknown;
  try {
    arr = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(arr)) return [];
  const logos: BrandingLogo[] = [];
  for (const item of arr) {
    if (typeof item !== 'object' || item === null) continue;
    const rec = item as Record<string, unknown>;
    const url = typeof rec.url === 'string' ? rec.url.trim() : '';
    if (url === '') continue; // une entrée sans URL n'est jamais affichée
    logos.push({ url, alt: typeof rec.alt === 'string' ? rec.alt.trim() : '' });
  }
  return logos;
}

/**
 * Renvoie la liste ordonnée des logos configurés (URL non vide). `[]` si
 * aucun / erreur.
 */
export function useBrandingLogos(): BrandingLogo[] {
  const query = useQuery<{ settings?: Record<string, unknown> } | null>({
    queryKey: ['site-settings', 'branding.header_logos'],
    queryFn: async () => {
      try {
        return await apiRequest<{ settings?: Record<string, unknown> }>(
          `me/site-settings.php?keys=${SETTING_KEY}`,
        );
      } catch {
        // Offline / endpoint absent / clé non seedée → aucun logo.
        return null;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  return useMemo(
    () => parseLogos(query.data?.settings?.[SETTING_KEY]),
    [query.data],
  );
}
